// components/TreatmentRound.tsx
import React, { CSSProperties } from 'react';
import { SeverityLevel, MedicationLogEntry } from '../utils/asthmaTypes'; // MildSectionKey removed as it is not directly used here anymore
import { getMaxDosesForSeverity } from '../utils/asthmaScoring';
import { confirmButton } from '../../../components/Buttons';
import TreatmentMedications from './TreatmentMedications';
import TreatmentTimer from './TreatmentTimer';
import { MedicationLog } from './MedicationLog';

interface TreatmentRoundProps {
  round: 1 | 2 | 3;
  activeSeverity: SeverityLevel;
  medicationLog: MedicationLogEntry[];
  dosesAtStartOfRound2: number;
  secondRoundStarted: boolean;
  thirdRoundStarted: boolean;
  recheckDone: boolean;
  hasGivenSteroids: boolean;
  setCurrentMedications: (meds: string[]) => void;
  handleDoseGiven: (date: Date) => void;
  onStartRecheck: () => void;
}

export const TreatmentRound: React.FC<TreatmentRoundProps> = ({
  round,
  activeSeverity,
  medicationLog,
  dosesAtStartOfRound2,
  secondRoundStarted,
  thirdRoundStarted,
  recheckDone,
  hasGivenSteroids,
  setCurrentMedications,
  handleDoseGiven,
  onStartRecheck
}) => {
  if (!activeSeverity) return <div>יש להשלים הערכה ראשונית.</div>;

  const isCurrentRoundActive = round === 1 ? true : round === 2 ? secondRoundStarted : thirdRoundStarted;
  const totalGivenDoses = medicationLog.length;
  let maxDoses = getMaxDosesForSeverity(activeSeverity, round);

  if (round === 2 && activeSeverity === 'SEVERE' && dosesAtStartOfRound2 === 3) {
    maxDoses = 4;
  }

  const anyGiven = totalGivenDoses > 0;
  const allRecommendedGiven = totalGivenDoses >= maxDoses;
  const showStatusOnly = recheckDone && (round === 1 || (round === 2 && secondRoundStarted) || (round === 3 && thirdRoundStarted));

  const renderSplitLayout = (mainContent: React.ReactNode) => (
    <div className="treatment-split-layout">
      <div className="treatment-main-content">{mainContent}</div>
      <div className="treatment-log-column">
        {medicationLog.length > 0 && <MedicationLog medicationLog={medicationLog} />}
      </div>
    </div>
  );

  // 1. status only display
  if (showStatusOnly && isCurrentRoundActive) {
    let statusText = !anyGiven ? 'לא ניתן טיפול תרופתי' : allRecommendedGiven ? 'כל המנות המומלצות ניתנו' : 'ניתנו רק חלק מהמנות המומלצות';
    return (
      <>
        {renderSplitLayout(
          <div className="treatment-dose-header">{statusText}</div>
        )}
      </>
    );
  }

  // 2. end of round display -> button to move to the full calculator
  if (allRecommendedGiven && isCurrentRoundActive) {
    return (
      <>
        {renderSplitLayout(
          <>
            <div className="treatment-dose-header">כל המנות המומלצות ניתנו</div>
            <p className="treatment-recheck-note">יש לבצע הערכה חוזרת.</p>
            <div className="treatment-recheck-cta">
              <button
                type="button"
                className="treatment-recheck-button"
                style={confirmButton as CSSProperties}
                onClick={onStartRecheck}
              >
                ביצוע הערכה חוזרת
              </button>
            </div>
          </>
        )}
      </>
    );
  }

  // 3. active round display
  const nextDoseNumber = Math.min(totalGivenDoses + 1, maxDoses);
  const renderHeaderContent = () => {
    const base = <>מנה {nextDoseNumber} מתוך {maxDoses}{activeSeverity !== 'MILD' && <span className="treatment-dose-note"> (במרווחים של 20 דק׳)</span>}</>;
    if (round === 1) return base;
    const prefix = round === 2 ? 'טיפול תרופתי – שני' : 'טיפול תרופתי – שלישי';
    return <>{prefix}<br />{base}</>;
  };

  return (
    <>
      {renderSplitLayout(
        <>
          <div className="treatment-dose-header">{renderHeaderContent()}</div>
          <TreatmentMedications
            onSelectionChange={setCurrentMedications}
            hasGivenSteroids={hasGivenSteroids}
          />
          <TreatmentTimer onDoseGiven={handleDoseGiven} />
        </>
      )}
    </>
  );
};